/**
 * The project map: its roadmap, plans, architecture and tasks, drawn from what the members'
 * workbenches mirror here (packages/shared/src/schemas/projectMap.ts) and from the shared
 * boards and memory the platform holds. The project's architecture is the workbench's picture
 * (architecture/ProjectArchitectureView.tsx); the rest is plain SVG: boxes and lines laid out
 * by what depends on what, the tone of the area it belongs to, and words for every state.
 */

import { dateMedium, monthYear } from '@aioe/shared/wording/dateTime';
import {
  effectiveRoadmapStatus,
  alongsideWords,
  effectiveTaskStatus,
  ownerNameOf,
  PLAN_STATUS_TEXT,
  PROJECT_BOARD_ID,
  PROJECT_LEVEL_ROOT_ID,
  ROADMAP_STATUS_TEXT,
  type Architecture,
  type MapTask,
  type OwnerRef,
  type Plan,
  type WorkClaimAlongside,
  type ProjectMapResponse,
  type ProjectRoadmapResponse,
  type ProjectRoadmapRowView,
  type ProjectRootMap,
  type RoadmapRow,
  taskStatusText,
} from '@aioe/shared';
import { LayoutCells, ListCheck, NodesRight, Route } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useEffect, useMemo, useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow, SectionHeading, StatusMark } from './ui.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { Markdown, plainText } from './Markdown.tsx';
import { OwnerPicker } from './OwnerPicker.tsx';
import { ProjectPictures } from './projectPictures.tsx';
import { ProjectGantt } from './roadmap/ProjectGantt.tsx';
import { ProjectArchitectureView } from './architecture/ProjectArchitectureView.tsx';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

type Mark = 'connected' | 'online' | 'working' | 'idle' | 'error' | 'medium' | 'disabled' | 'offline';

/** One tone per part of the map, as the workbench's own views have them. */
const TONES = { roadmap: 'green', plans: 'amber', architecture: 'rose', tasks: 'green' } as const;

const ROADMAP_MARK: Record<RoadmapRow['status'], Mark> = { 'not-started': 'idle', 'in-progress': 'working', done: 'connected', deferred: 'disabled', 'wont-do': 'offline' };
const PLAN_MARK: Record<Plan['status'], Mark> = { draft: 'idle', approved: 'online', dispatched: 'working', done: 'connected', verified: 'connected', abandoned: 'offline' };
const TASK_MARK = (status: string): Mark => (status === 'done' ? 'connected' : status === 'in-progress' ? 'working' : status === 'review' ? 'online' : status === 'blocked' ? 'error' : 'idle');
/** One status vocabulary with the workbench and the phone (statusWords.ts in shared). */
const TASK_TEXT = (status: string): string => (status ? taskStatusText(status) : '');

type MapView = 'map' | 'gantt';
const MAP_VIEW_KEY = 'aioe.projectMap.view';

function readMapView(): MapView {
  try {
    return localStorage.getItem(MAP_VIEW_KEY) === 'gantt' ? 'gantt' : 'map';
  } catch {
    return 'map';
  }
}

export function ProjectMapPanel({ projectId, canEdit = false }: { projectId: string; canEdit?: boolean }) {
  const map = useLoad(() => routes.projectMap(projectId), [projectId], 30_000);
  // The map as drawn, or the roadmap on dates (the workbench Roadmap's Timeline layout); remembered per viewer.
  const [view, setViewState] = useState<MapView>(readMapView);
  const setView = (next: MapView) => {
    setViewState(next);
    try {
      localStorage.setItem(MAP_VIEW_KEY, next);
    } catch {
      /* a per-viewer convenience only */
    }
  };
  const [rootId, setRootId] = useState<string>();
  /** The roadmap item a Gantt bar opened, shown on the map. */
  const [opened, setOpened] = useState<{ rootId: string; id: string }>();
  useEffect(() => {
    if (!opened || view !== 'map') return;
    const el = document.querySelector<HTMLElement>(`[data-roadmap-row="${CSS.escape(`${opened.rootId}/${opened.id}`)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.dataset.opened = 'true';
    const t = setTimeout(() => {
      delete el.dataset.opened;
      setOpened(undefined);
    }, 2400);
    return () => clearTimeout(t);
  }, [opened, view, map.data]);
  // Who works a row alongside whom (D247, C-8), from the working view; nothing here depends on it loading.
  const working = useLoad(() => routes.working(projectId).catch(() => undefined), [projectId], 30_000);
  const alongside = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const i of working.data?.items ?? []) if (i.kind === 'roadmap' && i.alongside?.length) m.set(`${i.ref.rootId}/${i.ref.id}`, i.alongside.map((a: WorkClaimAlongside) => alongsideWords(i.ref.id, a)));
    return m;
  }, [working.data]);
  const d = map.data;
  const roots = d?.roots ?? [];
  const root = roots.find((r) => r.rootId === rootId) ?? roots[0];
  const projectBoard = d?.boards.find((b) => b.rootId === PROJECT_LEVEL_ROOT_ID && b.board === PROJECT_BOARD_ID);
  const repositoryBoards = (d?.boards ?? []).filter((b) => b.rootId !== PROJECT_LEVEL_ROOT_ID);
  const boards = repositoryBoards.filter((b) => !root || roots.length <= 1 || b.rootId === root.rootId);
  const hasProject = Boolean(d?.project?.roadmap?.rows.length || d?.project?.architecture?.layers.length || d?.project?.architecture?.connections.length || projectBoard?.tasks.length);
  const picker =
    roots.length > 1 ? (
      <ChoiceSelect label="Repository" value={root?.rootId} options={Object.fromEntries(roots.map((r) => [r.rootId, r.rootId]))} onChange={setRootId} />
    ) : undefined;
  const hasRoadmap = Boolean(d?.project?.roadmap?.rows.length || roots.some((r) => r.roadmap?.length));
  return (
    <>
      <ErrorNote error={map.error} />
      {d && hasRoadmap && (
        <Segment aria-label="How the map is shown" size="sm" selectedKey={view} onSelectionChange={(k) => setView(String(k) === 'gantt' ? 'gantt' : 'map')} className="w-fit" data-project-map-view={view}>
          <Segment.Item id="map">Map</Segment.Item>
          <Segment.Item id="gantt">Gantt</Segment.Item>
        </Segment>
      )}
      {d && hasRoadmap && view === 'gantt' && (
        <ProjectGantt
          projectId={projectId}
          map={d}
          tone={TONES.roadmap}
          onOpen={(item) => {
            // A bar opens its item: the map, on that roadmap, with the item's card brought into view.
            if (item.rootId) setRootId(item.rootId);
            setView('map');
            setOpened(item);
          }}
        />
      )}
      {d && (!hasRoadmap || view === 'map') && (
        <>
          {d.project?.roadmap?.rows.length ? <ProjectRoadmapSection roadmap={d.project.roadmap} roots={roots} alongside={alongside} edit={canEdit ? { projectId, saved: map.reload } : undefined} /> : null}
          {projectBoard?.tasks.length ? <ProjectTasks projectId={projectId} tasks={projectBoard.tasks} boards={repositoryBoards} roots={roots} /> : null}
          {d.project?.architecture && (d.project.architecture.layers.length || d.project.architecture.connections.length) ? <ProjectArchitectureView doc={d.project.architecture} roots={roots} repositories={roots.map((r) => ({ rootId: r.rootId, ...(r.name ? { name: r.name } : {}) }))} /> : null}
          <ProjectPictures projectId={projectId} />
          {hasProject || picker ? <SectionHeading title="By repository" description={root ? `What a member's workbench last mirrored of ${root.name ?? root.rootId}: its roadmap, plans, architecture and tasks.` : "What the members' workbenches mirror of each repository."} actions={picker} /> : null}
          <Roadmap root={root} alongside={alongside} />
          <Plans root={root} tasks={boards.flatMap((b) => b.tasks)} everywhere={repositoryBoards} />
          <ArchitectureMap root={root} memory={d.memory} />
          <Tasks boards={boards} />
        </>
      )}
    </>
  );
}

const mirrored = (root: ProjectRootMap | undefined) => (root ? `Mirrored ${timeAgo(root.updatedAt)} at ${root.revision.slice(0, 12)}` : undefined);

/** The roadmap as the repository keeps it: one column per phase, one card per row, its status in words. */
function Roadmap({ root, alongside }: { root?: ProjectRootMap; alongside: Map<string, string[]> }) {
  const rows = useMemo(() => root?.roadmap ?? [], [root]);
  const phases = useMemo(() => {
    const m = new Map<string, RoadmapRow[]>();
    for (const r of rows) m.set(r.phase, [...(m.get(r.phase) ?? []), r]);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [rows]);
  const done = rows.filter((r) => r.status === 'done').length;
  return (
    <ListGroup
      title="Roadmap"
      count={rows.length || undefined}
      description={rows.length ? `${done} of ${rows.length} done · ${mirrored(root)}` : mirrored(root)}
      empty={rows.length === 0 ? <Empty tone={TONES.roadmap} icon={<Route />} title="No roadmap mirrored yet" body="A member's workbench sends the rows of the repository's roadmap the next time it syncs this project. Until then there is nothing to draw." compact /> : undefined}
    >
      <ListBlock flush>
        <div className="overflow-x-auto">
          <div className="flex min-w-max gap-3 p-4">
            {phases.map(([phase, items]) => (
              <div key={phase} className="w-64 shrink-0">
                <div className="mb-2 flex items-baseline justify-between text-xs">
                  <span className="font-medium">Phase {phase}</span>
                  <span className="text-muted tabular-nums">
                    {items.filter((r) => r.status === 'done').length}/{items.length}
                  </span>
                </div>
                <ul className="grid gap-2">
                  {items.map((r) => (
                    <li key={r.id} className="rounded-lg border border-separator bg-surface p-2.5 text-sm data-[opened]:ring-2 data-[opened]:ring-accent" title={r.evidence} data-roadmap-row={`${root?.rootId ?? ''}/${r.id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="mono text-xs text-muted">{r.id}</span>
                        <StatusMark state={ROADMAP_MARK[r.status]} label={ROADMAP_STATUS_TEXT[r.status]} />
                      </div>
                      <div className="mt-0.5 font-medium">{r.title}</div>
                      <div className="truncate text-xs text-muted" title={r.area}>
                        {r.area}
                        {r.dependsOn?.length ? ` · after ${r.dependsOn.join(', ')}` : ''}
                        {ownerNameOf(r.owner) ? ` · ${ownerNameOf(r.owner)}` : ''}
                      </div>
                      {alongside.get(`${root?.rootId ?? ''}/${r.id}`)?.map((w) => (
                        <div key={w} className="text-xs text-muted">
                          {w}
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </ListBlock>
    </ListGroup>
  );
}

/** The plans, one at a time: its nodes laid out by what depends on what, joined with the board's task states. */
function Plans({ root, tasks, everywhere }: { root?: ProjectRootMap; tasks: MapTask[]; everywhere: ProjectMapResponse['boards'] }) {
  const plans = root?.plans ?? [];
  const [pickedId, setPicked] = useState<string>();
  const plan = plans.find((p) => p.id === pickedId) ?? plans.find((p) => p.status === 'dispatched') ?? plans[0];
  const taskOf = new Map(tasks.map((t) => [t.id, t]));
  const nodes = useMemo(() => plan?.nodes ?? [], [plan]);
  const layout = useMemo(() => layered(nodes.map((n) => ({ id: n.ref, deps: n.dependsOn }))), [nodes]);
  const critical = new Set(plan?.criticalPath ?? []);
  // A step's task: on the plan's own board by its id, or, for a part done in another repository, on that repository's board by uid.
  type Node = (typeof nodes)[number];
  const taskFor = (n: Node | undefined): MapTask | undefined => (n?.task ? everywhere.find((b) => b.rootId === n.task!.rootId && b.board === n.task!.board)?.tasks.find((t) => t.uid === n.task!.uid) : n?.taskId ? taskOf.get(n.taskId) : undefined);
  const elsewhere = (n: Node | undefined) => {
    const where = n?.task?.rootId ?? n?.rootId;
    return where && where !== root?.rootId ? where : undefined;
  };
  const progress = plan?.progress ?? { planned: nodes.length, done: nodes.filter((n) => taskFor(n)?.status === 'done').length };
  return (
    <ListGroup
      title="Plans"
      count={plans.length || undefined}
      description={plan ? `${PLAN_STATUS_TEXT[plan.status]} · ${progress.done} of ${progress.planned} done${plan.acceptance.length ? ` · ${plan.acceptance.filter((a) => a.passed).length} of ${plan.acceptance.length} acceptance checks pass` : ''}` : mirrored(root)}
      actions={
        plans.length > 1 ? (
          <ChoiceSelect label="Plan" value={plan?.id} options={Object.fromEntries(plans.map((p) => [p.id, `${p.ref ? `${p.ref} · ` : ''}${p.objective.slice(0, 60)}`]))} onChange={setPicked} />
        ) : undefined
      }
      empty={!plan ? <Empty tone={TONES.plans} icon={<NodesRight />} title="No plan mirrored yet" body="Plans a team works through on a member's workbench are mirrored here as they are made and approved. Their steps are drawn by what depends on what, with each step's task state." compact /> : undefined}
    >
      {plan ? (
        <>
          <ListRow title={plan.objective} description={`${plan.ref ?? plan.id} · ${plan.teamName ?? plan.teamId} · ${plan.author}`} status={<StatusMark state={PLAN_MARK[plan.status]} label={PLAN_STATUS_TEXT[plan.status]} />} />
          <ListBlock flush>
          <Flow
            layout={layout}
            label={(id) => nodes.find((n) => n.ref === id)?.title ?? id}
            sub={(id) => {
              const n = nodes.find((x) => x.ref === id);
              const t = taskFor(n);
              const where = elsewhere(n);
              if (where) return t ? `in ${where} · ${t.id} · ${TASK_TEXT(t.status)}` : `in ${where} · not on its board yet`;
              return t ? `${t.id} · ${TASK_TEXT(t.status)}${t.assignee ? ` · ${t.assignee}` : ''}` : n?.assignee ?? n?.role ?? 'not on the board yet';
            }}
            mark={(id) => {
              const t = taskFor(nodes.find((x) => x.ref === id));
              return t ? TASK_MARK(t.status) : 'idle';
            }}
            strong={(from, to) => critical.has(from) && critical.has(to)}
            legend={[critical.size ? 'Thick lines: the critical path' : '', nodes.some((n) => elsewhere(n)) ? 'Steps marked "in …" are done in another repository of the project' : ''].filter(Boolean).join(' · ') || undefined}
          />
          </ListBlock>
          {plan.acceptance.map((a) => (
            <ListRow key={a.id} title={a.text} description={`Acceptance check ${a.id}`} status={<StatusMark state={a.passed ? 'connected' : 'idle'} label={a.passed ? 'Passes' : 'Not yet'} />} />
          ))}
        </>
      ) : null}
    </ListGroup>
  );
}

/** The architecture: layers as bands, areas as boxes, the heavier dependencies drawn between them; or, without a model, the memory graph's communities. */
function ArchitectureMap({ root, memory }: { root?: ProjectRootMap; memory: ProjectMapResponse['memory'] }) {
  const arch = root?.architecture;
  const communities = memory.communities.filter((c) => !root || memory.communities.every((x) => x.rootId === c.rootId) || c.rootId === root.rootId);
  return (
    <ListGroup
      title="Architecture"
      count={arch ? arch.areas.length : communities.length || undefined}
      description={arch ? `${arch.layers.length} layers, ${arch.areas.length} areas · ${mirrored(root)}` : communities.length ? `${communities.length} communities the memory graph found across ${memory.nodes.toLocaleString()} nodes` : mirrored(root)}
      empty={(arch && arch.areas.length) || communities.length ? undefined : <Empty tone={TONES.architecture} icon={<LayoutCells />} title="No map of the code yet" body="A member's workbench mirrors the repository's architecture model, its layers and areas, when it has one; the memory graph's communities are drawn until then." compact />}
    >
      <ListBlock flush>{arch && arch.areas.length ? <Layers arch={arch} /> : <Communities memory={{ ...memory, communities }} />}</ListBlock>
    </ListGroup>
  );
}

function Layers({ arch }: { arch: Architecture }) {
  const layers = arch.layers.length ? arch.layers : [{ id: '', title: 'Areas' }];
  const areasOf = (layerId: string) => arch.areas.filter((a) => (a.layer || '') === layerId);
  const orphans = arch.areas.filter((a) => !layers.some((l) => l.id === a.layer));
  const heaviest = new Map<string, { id: string; weight: number }>();
  for (const a of arch.areas) {
    const top = [...a.dependsOn].sort((x, y) => y.weight - x.weight)[0];
    if (top) heaviest.set(a.id, top);
  }
  const titleOf = (id: string) => arch.areas.find((a) => a.id === id)?.title ?? id;
  return (
    <div className="grid gap-px">
      {[...layers, ...(orphans.length ? [{ id: '\u0000other', title: 'Elsewhere' }] : [])].map((l) => {
        const areas = l.id === '\u0000other' ? orphans : areasOf(l.id);
        if (!areas.length) return null;
        return (
          <div key={l.id} className="grid gap-2 border-t border-separator px-4 py-3 first:border-t-0 lg:grid-cols-[10rem_1fr] lg:gap-4">
            <div>
              <div className="text-sm font-medium">{l.title}</div>
              {l.description && <div className="text-xs text-muted">{plainText(l.description)}</div>}
            </div>
            <ul className="flex flex-wrap gap-2">
              {areas.map((a) => {
                const dep = heaviest.get(a.id);
                return (
                  <li key={a.id} className="w-56 rounded-lg border border-separator bg-surface p-2.5 text-sm" title={a.description ? plainText(a.description) : a.path}>
                    <div className="truncate font-medium">{a.title}</div>
                    <div className="mono truncate text-xs text-muted">{a.path}</div>
                    <div className="truncate text-xs text-muted">
                      {a.files !== undefined ? `${a.files} files` : ''}
                      {a.dependsOn.length ? `${a.files !== undefined ? ' · ' : ''}uses ${dep ? titleOf(dep.id) : ''}${a.dependsOn.length > 1 ? ` +${a.dependsOn.length - 1}` : ''}` : ''}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/** The memory graph's communities on a ring, the heavier links between them drawn as chords. */
function Communities({ memory }: { memory: ProjectMapResponse['memory'] }) {
  const cs = memory.communities.slice(0, 24);
  const idx = new Map(cs.map((c, i) => [c.id, i]));
  const n = cs.length;
  const R = 42;
  const at = (i: number) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return { x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a) };
  };
  const max = Math.max(1, ...memory.links.map((l) => l.weight));
  const links = memory.links.filter((l) => idx.has(l.from) && idx.has(l.to)).slice(0, 80);
  const biggest = Math.max(1, ...cs.map((c) => c.members));
  return (
    <div className="grid gap-3 p-4 lg:grid-cols-[minmax(16rem,28rem)_1fr]">
      <svg viewBox="0 0 100 100" className="mx-auto aspect-square w-full max-w-md text-accent" role="img" aria-label="Communities and the links between them">
        {links.map((l) => {
          const a = at(idx.get(l.from)!);
          const b = at(idx.get(l.to)!);
          return <path key={`${l.from}-${l.to}`} d={`M${a.x},${a.y} Q50,50 ${b.x},${b.y}`} fill="none" stroke="currentColor" strokeWidth={0.3 + (l.weight / max) * 1.5} opacity={0.35} />;
        })}
        {cs.map((c, i) => {
          const p = at(i);
          const r = 1.6 + (c.members / biggest) * 3;
          return (
            <g key={c.id}>
              <circle cx={p.x} cy={p.y} r={r} fill="currentColor" opacity={0.9}>
                <title>{`${c.name}: ${c.members} members${c.summary ? `. ${plainText(c.summary)}` : ''}`}</title>
              </circle>
              <text x={p.x + (p.x >= 50 ? r + 1 : -(r + 1))} y={p.y + 1} fontSize="2.6" textAnchor={p.x >= 50 ? 'start' : 'end'} style={{ fill: 'var(--foreground)' }}>
                {c.name.length > 22 ? `${c.name.slice(0, 21)}…` : c.name}
              </text>
            </g>
          );
        })}
      </svg>
      <ul className="grid content-start gap-1 text-sm">
        {cs.map((c) => (
          <li key={c.id} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate" title={plainText(c.summary)}>
              {c.name}
            </span>
            <span className="shrink-0 text-xs text-muted tabular-nums">{c.members} members</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The shared boards' tasks laid out by what each waits on, with its state. */
function Tasks({ boards }: { boards: ProjectMapResponse['boards'] }) {
  const [pickedKey, setPicked] = useState<string>();
  const board = boards.find((b) => `${b.rootId}/${b.board}` === pickedKey) ?? boards[0];
  const open = useMemo(() => board?.tasks.filter((t) => t.status !== 'done') ?? [], [board]);
  const drawn = useMemo(() => {
    const ids = new Set(open.map((t) => t.id));
    return layered(open.map((t) => ({ id: t.id, deps: t.dependencies.filter((d) => ids.has(d)) })));
  }, [open]);
  const taskOf = new Map((board?.tasks ?? []).map((t) => [t.id, t]));
  return (
    <ListGroup
      title="Tasks"
      count={board ? board.tasks.length : undefined}
      description={board ? `${open.length} open · done tasks are left out of the drawing` : 'The shared boards the members’ workbenches keep here'}
      empty={!board ? <Empty tone={TONES.tasks} icon={<ListCheck />} title="No shared board yet" body="A workbench that keeps this project's board in the organisation creates one with its first task; the open tasks are drawn here by what each waits on." compact /> : undefined}
      actions={
        boards.length > 1 ? (
          <ChoiceSelect
            label="Board"
            value={board ? `${board.rootId}/${board.board}` : undefined}
            options={Object.fromEntries(boards.map((b) => [`${b.rootId}/${b.board}`, `${b.board === 'solo' ? 'Solo work' : b.board} · ${b.rootId}`]))}
            onChange={setPicked}
          />
        ) : undefined
      }
    >
      {open.length === 0 ? (
        <ListBlock className="text-sm text-muted">Every task on this board is done.</ListBlock>
      ) : (
        <ListBlock flush>
          <Flow layout={drawn} label={(id) => taskOf.get(id)?.title ?? id} sub={(id) => `${id} · ${TASK_TEXT(taskOf.get(id)?.status ?? '')}${taskOf.get(id)?.assignee ? ` · ${taskOf.get(id)!.assignee}` : ''}`} mark={(id) => TASK_MARK(taskOf.get(id)?.status ?? '')} />
        </ListBlock>
      )}
    </ListGroup>
  );
}

/* ---------- the project's own layer, above its repositories ---------- */

const SHOWN_ROADMAP_MARK: Record<RoadmapRow['status'] | 'blocked', Mark> = { ...ROADMAP_MARK, blocked: 'error' };
const SHOWN_ROADMAP_TEXT: Record<RoadmapRow['status'] | 'blocked', string> = { ...ROADMAP_STATUS_TEXT, blocked: 'Blocked' };

/** A roadmap target in words: "Nov 2026", "Q1 2027", "30 Nov 2026". */
export function targetText(t: string): string {
  const q = /^(\d{4})-Q([1-4])$/.exec(t);
  if (q) return `Q${q[2]} ${q[1]}`;
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(t);
  if (!m) return t;
  const at = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3] ?? 1));
  return m[3] ? dateMedium(at, { timeZone: 'UTC' }) : monthYear(at, { width: 'short', timeZone: 'UTC' });
}

/** The choices a target picker offers: none, the next six quarters, the next twelve months, and the current target whatever it is. */
function targetOptions(current?: string): Array<{ value: string; label: string }> {
  const now = new Date();
  const out = [{ value: '', label: 'No target' }];
  const y = now.getFullYear();
  const q0 = Math.floor(now.getMonth() / 3);
  for (let i = 0; i < 6; i++) {
    const q = (q0 + i) % 4;
    const value = `${y + Math.floor((q0 + i) / 4)}-Q${q + 1}`;
    out.push({ value, label: `Target ${targetText(value)}` });
  }
  for (let i = 0; i < 12; i++) {
    const d = new Date(y, now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value, label: `Target ${targetText(value)}` });
  }
  if (current && !out.some((o) => o.value === current)) out.splice(1, 0, { value: current, label: `Target ${targetText(current)}` });
  return out;
}

/** The project roadmap: one column per phase; each row with the repository rows it is delivered by, and what they add up to. */
function ProjectRoadmapSection({ roadmap, roots, edit, alongside }: { roadmap: ProjectRoadmapResponse; roots: ProjectRootMap[]; edit?: { projectId: string; saved: () => void }; alongside: Map<string, string[]> }) {
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState<string>();
  /** Change one row and write the roadmap back at the revision it was read at. */
  const setRow = async (rowId: string, change: { target: string } | { owner: OwnerRef | undefined }) => {
    if (!edit) return;
    setError(undefined);
    setSaving(rowId);
    try {
      const next = roadmap.rows.map(({ rolledUp: _r, ...r }) => {
        if (r.id !== rowId) return r;
        if ('target' in change) return { ...r, target: change.target };
        const { owner: _o, ...rest } = r;
        return change.owner ? { ...rest, owner: change.owner } : rest;
      });
      await routes.putProjectRoadmap(edit.projectId, roadmap.revision, { schemaVersion: 1, ...(roadmap.vision ? { vision: roadmap.vision } : {}), rows: next });
      edit.saved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(undefined);
    }
  };
  const rows = roadmap.rows;
  const phases = useMemo(() => {
    const m = new Map<string, ProjectRoadmapRowView[]>();
    for (const r of rows) m.set(r.phase, [...(m.get(r.phase) ?? []), r]);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [rows]);
  const repoRow = (rootId: string, id: string) => roots.find((r) => r.rootId === rootId)?.roadmap?.find((x) => x.id === id);
  const isDone = (r: ProjectRoadmapRowView) => effectiveRoadmapStatus(r) === 'done';
  return (
    <ListGroup title="Project roadmap" count={rows.length} description={`${rows.filter(isDone).length} of ${rows.length} done · rolled up from the repository rows each is delegated to${roadmap.updatedAt ? ` · changed ${timeAgo(roadmap.updatedAt)}` : ''}`}>
      {roadmap.vision ? (
        <ListBlock>
          <Markdown compact className="text-muted" text={roadmap.vision} />
        </ListBlock>
      ) : null}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      <ListBlock flush>
      <div className="overflow-x-auto">
        <div className="flex min-w-max gap-3 p-4">
          {phases.map(([phase, items]) => (
            <div key={phase} className="w-72 shrink-0">
              <div className="mb-2 flex items-baseline justify-between text-xs">
                <span className="font-medium">{phase ? `Phase ${phase}` : 'No phase yet'}</span>
                <span className="text-muted tabular-nums">
                  {items.filter(isDone).length}/{items.length}
                </span>
              </div>
              <ul className="grid grid-cols-[minmax(0,1fr)] gap-2">
                {items.map((r) => {
                  const shown = effectiveRoadmapStatus(r);
                  return (
                    <li key={r.id} className="min-w-0 rounded-lg border border-separator bg-surface p-2.5 text-sm data-[opened]:ring-2 data-[opened]:ring-accent" title={r.evidence} data-roadmap-row={`${PROJECT_LEVEL_ROOT_ID}/${r.id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="mono text-xs text-muted">{r.id}</span>
                        <StatusMark state={SHOWN_ROADMAP_MARK[shown]} label={SHOWN_ROADMAP_TEXT[shown]} />
                      </div>
                      <div className="mt-0.5 font-medium">{r.title}</div>
                      {edit ? (
                        <ChoiceSelect
                          label={`Target for ${r.id}`}
                          className="mt-1.5 w-full"
                          value={r.target || NONE}
                          isDisabled={saving === r.id}
                          options={Object.fromEntries(targetOptions(r.target).map((o) => [o.value || NONE, o.label]))}
                          onChange={(v) => void setRow(r.id, { target: v === NONE ? '' : v })}
                        />
                      ) : r.target ? (
                        <div className="text-xs text-muted">Target {targetText(r.target)}</div>
                      ) : null}
                      {edit ? <OwnerPicker projectId={edit.projectId} label={`Owner of ${r.id}`} className="mt-1.5 w-full" value={r.owner?.id ?? r.owner?.name} isDisabled={saving === r.id} onChange={(o) => void setRow(r.id, { owner: o })} /> : r.owner ? <div className="text-xs text-muted">{r.owner.name}</div> : null}
                      {alongside.get(`${PROJECT_LEVEL_ROOT_ID}/${r.id}`)?.map((w) => (
                        <div key={w} className="text-xs text-muted">
                          {w}
                        </div>
                      ))}
                      {r.delegates.length > 0 ? (
                        <ul className="mt-1.5 grid grid-cols-[minmax(0,1fr)] gap-0.5 border-t border-separator pt-1.5 text-xs text-muted">
                          {r.delegates.map((d) => {
                            const row = repoRow(d.rootId, d.id);
                            return (
                              <li key={`${d.rootId}/${d.id}`} className="flex min-w-0 items-baseline justify-between gap-2">
                                <span className="min-w-0 truncate" title={row?.title}>
                                  {d.rootId} · {d.id}
                                  {row ? ` ${row.title}` : ''}
                                </span>
                                <span className="shrink-0">{row ? ROADMAP_STATUS_TEXT[row.status] : 'not mirrored'}</span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="truncate text-xs text-muted">{r.area ? `${r.area} · ` : ''}not delegated yet</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
      </ListBlock>
    </ListGroup>
  );
}

/** The project's tasks: each one a piece of work delegated to repository tasks, with what they add up to. */
/** "cancelled", "handed on" or the task's status in words: done means done, so a closure is said. */
const PART_TEXT = (status: string, closure?: { kind: string }): string => (closure && closure.kind !== 'done' ? ({ cancelled: 'Cancelled', refused: 'Refused', 'handed-on': 'Handed on', blocked: 'Blocked' } as Record<string, string>)[closure.kind] ?? closure.kind : TASK_TEXT(status));

function ProjectTasks({ projectId, tasks, boards, roots }: { projectId: string; tasks: MapTask[]; boards: ProjectMapResponse['boards']; roots: ProjectRootMap[] }) {
  const parts = useLoad(() => routes.projectParts(projectId).catch(() => ({ parts: [] })), [projectId], 30_000);
  const rootName = (id: string) => roots.find((r) => r.rootId === id)?.name ?? id;
  const find = (rootId: string, board: string, uid: string) => boards.find((b) => b.rootId === rootId && b.board === board)?.tasks.find((t) => t.uid === uid);
  const shown = (t: MapTask) => effectiveTaskStatus({ status: t.status, metadata: { delegates: t.delegates, rollup: t.rollup } });
  const open = tasks.filter((t) => shown(t) !== 'done').length;
  return (
    <ListGroup title="Project tasks" count={tasks.length} description={`${open} open · each is worked through the repository tasks it is delegated to`}>
        {tasks.map((t) => (
          <ListRow
            key={t.id}
            title={t.title}
            description={t.delegates?.length ? t.id : `${t.id} · not delegated to a repository yet`}
            status={<StatusMark state={TASK_MARK(shown(t))} label={`${TASK_TEXT(shown(t))}${t.rollup?.reason === 'part_cancelled' ? ', a part was cancelled' : shown(t) !== t.status ? ', rolled up' : ''}`} />}
          >
            {t.delegates?.length ? (
              <ul className="m-0 grid list-none gap-0.5 p-0 text-xs text-muted @[40rem]:grid-cols-2">
                {t.delegates.map((d) => {
                  const task = find(d.rootId, d.board, d.uid);
                  return (
                    <li key={`${d.rootId}/${d.board}/${d.uid}`} className="flex items-baseline justify-between gap-2 sm:pr-4">
                      <span className="min-w-0 truncate" title={task?.title}>
                        {rootName(d.rootId)} · {task ? task.title : d.uid}
                      </span>
                      <span className="shrink-0">{task ? PART_TEXT(task.status, task.closure) : 'not on its board'}</span>
                    </li>
                  );
                })}
                {(parts.data?.parts ?? []).filter((p) => p.projectTask === t.uid && p.state !== 'placed').map((p) => (
                  <li key={p.partId} className="flex items-baseline justify-between gap-2 sm:pr-4">
                    <span className="min-w-0 truncate">
                      {rootName(p.rootId)} · {p.title}
                    </span>
                    <span className="shrink-0">{`waiting for a machine with ${rootName(p.rootId)}`}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </ListRow>
        ))}
    </ListGroup>
  );
}

/* ---------- the drawing ---------- */

interface Layout {
  columns: string[][];
  edges: Array<{ from: string; to: string }>;
}

/** Lay nodes out in columns by the longest chain of dependencies before each; a cycle is cut where it closes. */
function layered(nodes: Array<{ id: string; deps: string[] }>): Layout {
  const depth = new Map<string, number>();
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const visiting = new Set<string>();
  const edges: Array<{ from: string; to: string }> = [];
  const d = (id: string): number => {
    if (depth.has(id)) return depth.get(id)!;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const n = byId.get(id);
    let best = 0;
    for (const dep of n?.deps ?? []) if (byId.has(dep) && !visiting.has(dep)) best = Math.max(best, d(dep) + 1);
    visiting.delete(id);
    depth.set(id, best);
    return best;
  };
  for (const n of nodes) d(n.id);
  for (const n of nodes) for (const dep of n.deps) if (byId.has(dep) && (depth.get(dep) ?? 0) < (depth.get(n.id) ?? 0)) edges.push({ from: dep, to: n.id });
  const columns: string[][] = [];
  for (const n of nodes) {
    const c = depth.get(n.id) ?? 0;
    (columns[c] ??= []).push(n.id);
  }
  return { columns: columns.map((c) => c ?? []), edges };
}

const BOX_W = 200;
const BOX_H = 44;
const GAP_X = 56;
const GAP_Y = 12;

/** Boxes in columns, lines from what a thing waits on to it; the box's state as a dot and words. */
function Flow({ layout, label, sub, mark, strong, legend }: { layout: Layout; label: (id: string) => string; sub: (id: string) => string; mark: (id: string) => Mark; strong?: (from: string, to: string) => boolean; legend?: string }) {
  const pos = new Map<string, { x: number; y: number }>();
  const rows = Math.max(1, ...layout.columns.map((c) => c.length));
  layout.columns.forEach((col, ci) => col.forEach((id, ri) => pos.set(id, { x: ci * (BOX_W + GAP_X), y: ri * (BOX_H + GAP_Y) })));
  const width = layout.columns.length * (BOX_W + GAP_X) - GAP_X;
  const height = rows * (BOX_H + GAP_Y) - GAP_Y;
  const colour: Record<Mark, string> = { connected: 'var(--success)', online: 'var(--accent)', working: 'var(--warning)', idle: 'var(--muted)', error: 'var(--danger)', medium: 'var(--warning)', disabled: 'var(--muted)', offline: 'var(--muted)' };
  const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
  return (
    <div className="overflow-x-auto p-4">
      <svg width={Math.max(width, 1)} height={Math.max(height, 1)} viewBox={`0 0 ${Math.max(width, 1)} ${Math.max(height, 1)}`} className="block" role="img" aria-label="Steps, laid out by what each waits on">
        {layout.edges.map((e) => {
          const a = pos.get(e.from)!;
          const b = pos.get(e.to)!;
          const x1 = a.x + BOX_W;
          const y1 = a.y + BOX_H / 2;
          const x2 = b.x;
          const y2 = b.y + BOX_H / 2;
          const mx = (x1 + x2) / 2;
          const thick = strong?.(e.from, e.to);
          return <path key={`${e.from}-${e.to}`} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke="currentColor" strokeWidth={thick ? 2.5 : 1} opacity={thick ? 0.9 : 0.4} className="text-muted" />;
        })}
        {[...pos.entries()].map(([id, p]) => (
          <g key={id} transform={`translate(${p.x},${p.y})`}>
            <rect width={BOX_W} height={BOX_H} rx={8} style={{ fill: 'var(--surface)', stroke: 'var(--separator)' }} strokeWidth={1}>
              <title>{`${label(id)}\n${sub(id)}`}</title>
            </rect>
            <circle cx={14} cy={BOX_H / 2} r={4} fill={colour[mark(id)]} />
            <text x={26} y={18} fontSize="12" fontWeight={600} style={{ fill: 'var(--foreground)' }}>
              {clip(label(id), 26)}
            </text>
            <text x={26} y={33} fontSize="10.5" style={{ fill: 'var(--muted)' }}>
              {clip(sub(id), 32)}
            </text>
          </g>
        ))}
      </svg>
      {legend && <div className="mt-2 text-xs text-muted">{legend}</div>}
    </div>
  );
}
